import Grid from "@mui/material/Grid";
import {useEffect, useRef, useState} from "react";
import {Event} from "../../../../data/models/bracket_api/Event";
import {Bracket} from "../../../../data/models/bracket_api/Bracket";
import {Result} from "../../../../data/models/bracket_api/Result";
import {Pick} from "../../../../data/models/bracket_api/Pick";
import {RaceClass} from "../../../../data/models/bracket_api/RaceClass";
import PickBracketTopMoments from "./PickBracketTopMoments";
import {isTopMomentsRaceClass} from "../../../../data/models/bracket_api/RaceClass";

type PickBracketContainerTopMomentsProps = {
    picks_locked: boolean
    brackets?: Bracket[],
    updatePick: (pick: Pick) => void,
    results?: Result[]
    picks?: Pick[],
    event?: Event
}

export default function PickBracketContainerTopMoments(props: Readonly<PickBracketContainerTopMomentsProps>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState<boolean>(true);

    useEffect(() => {
        const container = containerRef.current;

        let isMouseDown = false;
        let startX: number;
        let scrollLeft: number;

        const element = containerRef.current;

        if (element) {
            setIsOverflowing(element.scrollWidth > element.clientWidth);
        }

        const handleMouseDown = (event: MouseEvent) => {
            isMouseDown = true;
            startX = event.pageX - container!.offsetLeft;
            scrollLeft = container!.scrollLeft;
        };

        const handleMouseUp = () => {
            isMouseDown = false;
        };

        const handleMouseMove = (event: MouseEvent) => {
            if (!isMouseDown) return;
            event.preventDefault();
            const x = event.pageX - container!.offsetLeft;
            const walk = (x - startX); // Adjust the scroll speed here
            container!.scrollLeft = scrollLeft - walk;
        };

        const handleResize = () => {
            const element = containerRef.current;

            if (element) {
                setIsOverflowing(element.scrollWidth > element.clientWidth);
            }
        }

        window.addEventListener('resize', handleResize);
        container!.addEventListener('mousedown', handleMouseDown);
        container!.addEventListener('mouseup', handleMouseUp);
        container!.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('resize', handleResize);
            container!.removeEventListener('mousedown', handleMouseDown);
            container!.removeEventListener('mouseup', handleMouseUp);
            container!.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    // Find the Top Moments bracket (there should only be one)
    let topMomentsBracket = props.brackets?.find((bracket) => {
        return isTopMomentsRaceClass(bracket.race_class);
    });
    
    let topMomentsResults = props.results?.filter((result) => {
        return result.bracket && isTopMomentsRaceClass(result.bracket.race_class);
    });

    let topMomentsPicks = props.picks?.filter((pick) => {
        return pick.result.bracket && isTopMomentsRaceClass(pick.result.bracket.race_class);
    });

    return (
        <Grid
            container
            ref={containerRef}
            sx={{
                width: 'fit-content',
                overflow: 'auto',
                cursor: isOverflowing ? 'grab' : 'default',
                userSelect: 'none',
            }}
        >
            <Grid container sx={{
                display: 'flex',
                flexDirection: "column",
                justifyContent: "space-evenly",
                alignItems: "center",
                alignContent: "center",
                height: 'fit-content',
                width: 'fit-content',
            }}>
                {/* Top row: Quadrants 0 and 1 */}
                <Grid container sx={{
                    marginY: 1,
                    display: 'flex',
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    width: 'fit-content',
                }}>
                    {/* Quadrant 0: Top Left */}
                    <PickBracketTopMoments
                        quadrant={0}
                        bracket={topMomentsBracket}
                        results={topMomentsResults}
                        picks={topMomentsPicks}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    {/* Quadrant 1: Top Right */}
                    <PickBracketTopMoments
                        quadrant={1}
                        bracket={topMomentsBracket}
                        results={topMomentsResults}
                        picks={topMomentsPicks}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
                
                {/* Center section: Rounds 5-6 (Final Four and Championship) */}
                <Grid container sx={{
                    marginY: 1,
                    display: 'flex',
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    width: 'fit-content',
                    justifyContent: "center",
                    alignItems: "center",
                }}>
                    <PickBracketTopMoments
                        quadrant={undefined}
                        bracket={topMomentsBracket}
                        results={topMomentsResults}
                        picks={topMomentsPicks}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                        event={props.event}
                    />
                </Grid>
                
                {/* Bottom row: Quadrants 2 and 3 */}
                <Grid container sx={{
                    marginY: 1,
                    display: 'flex',
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    width: 'fit-content',
                }}>
                    {/* Quadrant 2: Bottom Left */}
                    <PickBracketTopMoments
                        quadrant={2}
                        bracket={topMomentsBracket}
                        results={topMomentsResults}
                        picks={topMomentsPicks}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                    {/* Quadrant 3: Bottom Right */}
                    <PickBracketTopMoments
                        quadrant={3}
                        bracket={topMomentsBracket}
                        results={topMomentsResults}
                        picks={topMomentsPicks}
                        updatePick={props.updatePick}
                        picks_locked={props.picks_locked}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
}
